import { useState, type ReactNode } from 'react';
import { AlertTriangle, Check, CheckCircle2, ChevronRight, Clock, Copy, FlaskConical, ShieldCheck } from 'lucide-react';
import { OverflowMenu, type MenuItem } from '../ui/menu';
import { cn } from '../../lib/utils';
import type { Freshness, Health } from './model';

/** One labelled value. `copy` adds a copy button (endpoint URLs, event lists); never pass a secret. */
export interface Fact { label: string; value: ReactNode; wide?: boolean; copy?: string }
export type CardMessage = { kind: 'success' | 'error'; text: string } | null;

/** In place of a disabled button: says plainly that a person with host access must set this up first. */
export function BlockedAction({ label = 'Admin setup needed' }: { label?: string }) {
  return <span className="integration-blocked" role="note"><Clock size={13} aria-hidden/>{label}</span>;
}

export function StatusPill({ health }: { health: Health }) {
  return <span className={`integration-pill tone-${health.tone}`}><span aria-hidden/>{health.label}</span>;
}

export function Logo({ src, fill }: { src: string; fill?: boolean | undefined }) {
  return <span className={cn('integration-logo', fill && 'fill')}><img src={src} alt="" width={44} height={44}/></span>;
}

export function EnvironmentBadge({ environment }: { environment: string | null }) {
  if (!environment) return <span className="muted">Not set</span>;
  return <span className={`env-badge env-${environment}`}>{environment === 'production' ? 'Production' : 'Sandbox'}</span>;
}

/** Copies a public value (a URL, an event list) and says so for a moment. */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); window.setTimeout(() => setCopied(false), 1600); }
    catch { setCopied(false); }
  };
  return <button type="button" className="copy-button" onClick={() => void copy()} aria-label={`Copy ${label}`}>
    {copied ? <Check size={13} aria-hidden/> : <Copy size={13} aria-hidden/>}<span>{copied ? 'Copied' : 'Copy'}</span>
  </button>;
}

export function FactList({ facts }: { facts: Fact[] }) {
  if (!facts.length) return null;
  return <dl className="integration-facts">
    {facts.map((fact, index) => <div key={`${fact.label}-${index}`} className={cn(fact.wide && 'wide')}>
      <dt>{fact.label}</dt><dd><span className="fact-value">{fact.value}</span>
        {fact.copy && <CopyButton value={fact.copy} label={fact.label}/>}</dd></div>)}
  </dl>;
}

/**
 * The shell every connector card shares: logo, name and role, status, key facts, the next step when something is wrong,
 * a reassurance line, and an action row with a primary action, optional extra actions and an overflow menu.
 *
 * Slots for features that live on one card: `actions` joins the action row (for example "Send test email") and
 * `children` renders extra status rows between the facts and the actions.
 */
export function IntegrationCard({ id, logo, logoFill, name, role, health, summary, freshness, badge, keyFacts = [], facts,
  details, reassurance, message, primary, actions, menu = [], children }: {
  id: string; logo: string; logoFill?: boolean | undefined; name: string; role: string; health: Health;
  /** One plain sentence on what this connection does here. */
  summary?: string | undefined;
  /** "Updated 3 min ago · checks every 15 min"; amber when stale. */
  freshness?: Freshness | null;
  /** An honest label such as "Sandbox copy", with the sentence that explains it. */
  badge?: { label: string; text: string } | null;
  /** Facts a person needs at a glance (mailbox, company). */
  keyFacts?: Fact[];
  /** Technical facts (environment, masked ids, endpoint URL) that live in the collapsed Details section. */
  facts: Fact[];
  details?: ReactNode;
  reassurance?: string; message?: CardMessage; primary?: ReactNode; actions?: ReactNode; menu?: MenuItem[];
  children?: ReactNode;
}) {
  const titleId = `${id}-title`;
  return <article className={`integration-card tone-${health.tone}`} aria-labelledby={titleId} id={`integration-${id}`}>
    <header className="integration-head">
      <Logo src={logo} fill={logoFill}/>
      <div className="integration-title"><h3 id={titleId}>{name}</h3><p>{role}</p></div>
      <StatusPill health={health}/>
    </header>
    {summary && <p className="integration-summary">{summary}</p>}
    {freshness && <p className={cn('integration-freshness', freshness.stale && 'stale')} data-testid={`${id}-freshness`}>
      <Clock size={14} aria-hidden/><span>{freshness.text}</span></p>}
    {badge && <p className="integration-badge"><span className="env-badge env-sandbox"><FlaskConical size={12} aria-hidden/>
      {badge.label}</span><span>{badge.text}</span></p>}
    {health.detail && <p className="integration-next">
      {health.tone === 'attention' ? <AlertTriangle size={15} aria-hidden/> : null}<span>{health.detail}</span></p>}
    <FactList facts={keyFacts}/>
    {children && <div className="integration-extra">{children}</div>}
    {(facts.length > 0 || details || reassurance) && <details className="integration-details">
      <summary><ChevronRight size={14} aria-hidden/>Details</summary>
      <div className="integration-details-body">
        {/* The safety note lives with the details, so the action row carries only actions. */}
        {reassurance && <p className="integration-assurance"><ShieldCheck size={14} aria-hidden/>{reassurance}</p>}
        <FactList facts={facts}/>{details}</div>
    </details>}
    <div className="integration-message" role="status" aria-live="polite">
      {message?.kind === 'success' && <p className="ok"><CheckCircle2 size={15} aria-hidden/>{message.text}</p>}
    </div>
    {message?.kind === 'error' && <p className="integration-error" role="alert">{message.text}</p>}
    <footer className="integration-foot">
      <div className="integration-actions">{actions}{primary}<OverflowMenu label={`More ${name} actions`} items={menu}/></div>
    </footer>
  </article>;
}

/** Placeholder with the card's real proportions, so nothing shifts when the data arrives. */
export function CardSkeleton() {
  return <div className="integration-card skeleton" aria-hidden>
    <div className="integration-head"><span className="integration-logo"/><div className="integration-title">
      <span className="bone w-28"/><span className="bone w-40"/></div><span className="bone pill"/></div>
    <div className="integration-facts">{[0, 1, 2, 3].map(n => <span key={n} className="bone"/>)}</div>
    <div className="integration-foot"><span className="bone w-48"/><span className="bone button"/></div>
  </div>;
}

export function IntegrationSection({ id, title, description, aside, children }: {
  id: string; title: string; description: string; aside?: ReactNode; children: ReactNode;
}) {
  return <section className="integration-section" aria-labelledby={`${id}-heading`}>
    <div className="integration-section-head">
      <div><h2 id={`${id}-heading`}>{title}</h2><p>{description}</p></div>{aside}
    </div>
    <div className="integration-grid">{children}</div>
  </section>;
}
